import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from "react-router-dom";
import { trackPageView } from "./utils/analytics";

// Components
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import StickyBar from "./components/StickyBar";
import { WhatsAppCommunityRibbon } from "./components/WhatsAppCommunity";

// AuthContext kept for legacy support
import { AuthProvider } from "./contexts/AuthContext";

// Public pages
import HomePage from "./pages/HomePage";
import AcademyPage from "./pages/AcademyPage";
import CateringOperationsTrainingPage from "./pages/CateringOperationsTrainingPage";
import CateringOperationsApplicationPage from "./pages/CateringOperationsApplicationPage";
import ConsultingPage from "./pages/ConsultingPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import CareersPage from "./pages/CareersPage";
import CareersAdminPage from "./pages/CareersAdminPage";
import AirlineTransitionPage from "./pages/transition/AirlineTransitionPage";
import TransitionsHubPage from "./pages/transition/TransitionsHubPage";
import SEOPage from "./pages/insights/SEOPage";
import InsightsPage from "./pages/InsightsPage";
import EnrollmentPage from "./pages/EnrollmentPage";
import MentorshipPage from "./pages/MentorshipPage";
import InsightArticlePage from "./pages/InsightArticlePage";
import OperationalAssessmentPage from "./pages/OperationalAssessmentPage";
import LogoPage from "./pages/LogoPage";
import FAQPage from "./pages/FAQPage";
import LegalNoticePage from "./pages/LegalNoticePage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import CookiesPage from "./pages/CookiesPage";
import DeliveryReturnsPage from "./pages/DeliveryReturnsPage";
import WithdrawalFormPage from "./pages/WithdrawalFormPage";
import MasterclassPage from "./pages/MasterclassPage";
import TransitionsIndexPage from "./pages/TransitionsIndexPage";
import TransitionSegmentPage from "./pages/TransitionSegmentPage";
import TransitionRolePage from "./pages/TransitionRolePage";
import CookieBanner from "./components/CookieBanner";
import { CheckoutConsentGuard } from "./components/CheckoutConsentGuard";
import { HeadSync } from "./components/HeadSync";

// Confidential program — single password-protected training experience
import Gate33Program from "./pages/gate33-program/Gate33Program";
import Gate33Catering from "./pages/gate33-catering/Gate33Catering";
import Gate33CateringRef from "./pages/gate33-catering-ref/Gate33CateringRef";

// SEO Data
import { cateringPages } from "./data/seo-catering";
import { cateringExtraPages } from "./data/seo-catering-extra";
import { transitionPages } from "./data/seo-transition";
import { airportPages } from "./data/seo-airports";
import { airportExtraPages } from "./data/seo-airports-extra";
import { airportPages2 } from "./data/seo-airports-2";
import { airportsBatch3 } from "./data/seo-airports-batch3";
import { airportsBatch4 } from "./data/seo-airports-batch4";
import { authorityPages } from "./data/seo-authority";
import { aircraftPages } from "./data/seo-aircraft";
import { trainingPages } from "./data/seo-training";
import { hospitalityPages } from "./data/seo-hospitality";

const allSEOPages = [
  ...cateringPages.map(p => ({ ...p, basePath: 'catering' })),
  ...cateringExtraPages.map(p => ({ ...p, basePath: 'catering' })),
  ...aircraftPages.map(p => ({ ...p, basePath: 'catering' })),
  ...transitionPages.map(p => ({ ...p, basePath: 'transition' })),
  ...trainingPages.map(p => ({ ...p, basePath: 'transition' })),
  ...airportPages.map(p => ({ ...p, basePath: 'airports' })),
  ...airportExtraPages.map(p => ({ ...p, basePath: 'airports' })),
  ...airportPages2.map(p => ({ ...p, basePath: 'airports' })),
  ...airportsBatch3.map(p => ({ ...p, basePath: 'airports' })),
  ...airportsBatch4.map(p => ({ ...p, basePath: 'airports' })),
  ...authorityPages.map(p => ({ ...p, basePath: 'authority' })),
  ...hospitalityPages.map(p => ({ ...p, basePath: 'hospitality' })),
];

// Scroll to top on route change (unless hash present)
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      });
    }
    trackPageView(pathname + (hash || ''));
  }, [pathname, hash]);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  return null;
};

const STANDALONE_PREFIXES = ['/galleypilot/', '/gate33-program', '/gate33-catering-and-operations-training'];

const Layout = ({ children }) => {
  const isStandalone = typeof window !== 'undefined' &&
    STANDALONE_PREFIXES.some(p => window.location.pathname.startsWith(p));
  return (
    <div className="min-h-screen flex flex-col">
      {!isStandalone && <StickyBar />}
      {!isStandalone && <Navbar />}
      <main className="flex-1">{children}</main>
      {!isStandalone && <WhatsAppCommunityRibbon />}
      {!isStandalone && <Footer />}
      <CookieBanner />
      <CheckoutConsentGuard />
      <HeadSync />
    </div>
  );
};

const SEORouteResolver = () => {
  const { category, slug } = useParams();
  const page = allSEOPages.find(p => p.basePath === category && p.slug === slug);
  if (!page) return <HomePage />;
  return <SEOPage data={page} />;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollToTop />
        <Routes>
          {/* Confidential password-protected program (in-person + e-learning merged) */}
          <Route path="/gate33-program/*" element={<Gate33Program />} />

          {/* Confidential 1-day Operations & Catering training */}
          <Route path="/gate33-catering-and-operations-training/*" element={<Gate33Catering />} />

          {/* Confidential Catering & Ops private reference (10 steps) */}
          <Route path="/gate33-catering/*" element={<Gate33CateringRef />} />

          {/* Removed routes — redirect to home or training */}
          <Route path="/academy-online/*" element={<Navigate to="/" replace />} />
          <Route path="/secret-access" element={<Navigate to="/" replace />} />
          <Route path="/admin-ops/*" element={<Navigate to="/" replace />} />
          <Route path="/pathway" element={<Navigate to="/academy" replace />} />
          <Route path="/galleypilot" element={<Navigate to="/academy" replace />} />
          <Route path="/galleypilot/*" element={<Navigate to="/academy" replace />} />
          <Route path="/catering-ai" element={<Navigate to="/academy/catering-operations" replace />} />
          <Route path="/book" element={<Navigate to="/academy" replace />} />
          <Route path="/program-application" element={<Navigate to="/academy" replace />} />
          <Route path="/services" element={<Navigate to="/academy" replace />} />

          {/* Main public site with navbar/footer */}
          <Route path="*" element={
            <Layout>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/academy" element={<AcademyPage />} />
                <Route path="/academy/catering-operations" element={<CateringOperationsTrainingPage />} />
                <Route path="/training" element={<Navigate to="/academy" replace />} />
                <Route path="/mentorship" element={<MentorshipPage />} />
                <Route path="/operational-assessment" element={<OperationalAssessmentPage />} />
                <Route path="/enrollment" element={<EnrollmentPage />} />
                <Route path="/logo" element={<LogoPage />} />
                <Route path="/program-application/catering-operations" element={<CateringOperationsApplicationPage />} />
                <Route path="/consulting" element={<ConsultingPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/careers" element={<CareersPage />} />
                <Route path="/admin/careers" element={<CareersAdminPage />} />
                <Route path="/insights" element={<InsightsPage />} />
                <Route path="/insights/field-notes/:slug" element={<InsightArticlePage />} />
                <Route path="/insights/:category/:slug" element={<SEORouteResolver />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/masterclass" element={<MasterclassPage />} />
                <Route path="/transitions" element={<TransitionsIndexPage />} />
                <Route path="/transitions/:segment" element={<TransitionSegmentPage />} />
                <Route path="/transitions/:segment/:role" element={<TransitionRolePage />} />
                <Route path="/legal/mentions-legales" element={<LegalNoticePage />} />
                <Route path="/legal/legal-notice" element={<LegalNoticePage />} />
                <Route path="/legal/terms" element={<TermsPage />} />
                <Route path="/legal/cgv" element={<TermsPage />} />
                <Route path="/legal/privacy" element={<PrivacyPage />} />
                <Route path="/legal/rgpd" element={<PrivacyPage />} />
                <Route path="/legal/cookies" element={<CookiesPage />} />
                <Route path="/legal/delivery-returns" element={<DeliveryReturnsPage />} />
                <Route path="/legal/withdrawal-form" element={<WithdrawalFormPage />} />

                {/* Airline transition landing pages */}
                <Route path="/transition" element={<TransitionsHubPage />} />
                <Route path="/transition/:airline" element={<AirlineTransitionPage />} />
                <Route path="/transition/:airline/:lang" element={<AirlineTransitionPage />} />
              </Routes>
            </Layout>
          } />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
